import { apiWrite } from "@/api/client";
import type { ConfirmRefusal } from "@/components/ConfirmDialog";
import {
  doneState,
  errorState,
  type FormState,
  parseFormData,
} from "@/lib/formState";
import {
  type InlineCreateResult,
  inlineFieldError,
  parseInline,
} from "@shared/inlineCreate";
import { formatTemplateRefs } from "@shared/usage";
import {
  skillCreateBody,
  skillSchema,
  skillUpdateSchema,
} from "@shared/validation";

// Same signatures the server actions had, so every `useActionState` form and
// every RowActions binding is unchanged. What used to be a query call is now a
// request to the same rule enforced in one place, and what used to be
// `redirect()` is a "done" state the form's `useFormRedirect` acts on.

export async function createSkillAction(
  _prevState: FormState,
  formData: FormData,
): Promise<FormState> {
  const parsed = parseFormData(skillSchema, formData);

  if (!parsed.wasParsed) {
    return parsed.state;
  }

  const result = await apiWrite<{ id: number }>("/skills", {
    method: "POST",
    body: parsed.value,
  });

  if (!result.isOk) {
    return errorState(parsed.values, {
      // An archived skill still holds its code, and the plain message would be
      // baffling next to a listing that shows nothing active under it.
      code: ["A skill with this code already exists (it may be archived)."],
    });
  }

  return doneState("/skills");
}

export type SkillInlineChoice = {
  id: number;
  code: string;
  label: string;
  description: string;
};

// Minted from the quest template form, where a missing skill otherwise costs a
// round trip through /skills and a half-filled template.
export async function createSkillInlineAction(input: {
  code: string;
  label: string;
  // Optional in the signature, required by the schema: the dialog omits the
  // field entirely for row types that have no prose, and an absent one has to
  // come back as "Description is required." rather than as a type error.
  description?: string;
}): Promise<InlineCreateResult<SkillInlineChoice>> {
  const parsed = parseInline(skillCreateBody, input);

  if (!parsed.wasParsed) {
    return { wasCreated: false, fieldErrors: parsed.fieldErrors };
  }

  const result = await apiWrite<{ id: number }>("/skills", {
    method: "POST",
    body: parsed.value,
  });

  if (!result.isOk) {
    return inlineFieldError(
      "code",
      "A skill with this code already exists (it may be archived).",
    );
  }

  return {
    wasCreated: true,
    choice: {
      id: result.value.id,
      code: parsed.value.code,
      label: parsed.value.label,
      description: parsed.value.description,
    },
  };
}

export async function updateSkillAction(
  id: number,
  _prevState: FormState,
  formData: FormData,
): Promise<FormState> {
  const parsed = parseFormData(skillUpdateSchema, formData);

  if (!parsed.wasParsed) {
    return parsed.state;
  }

  await apiWrite(`/skills/${id}`, { method: "PUT", body: parsed.value });

  return doneState("/skills");
}

export async function archiveSkillAction(id: number): Promise<ConfirmRefusal> {
  await apiWrite(`/skills/${id}/deactivate`, { method: "POST" });

  return null;
}

export async function restoreSkillAction(id: number): Promise<void> {
  await apiWrite(`/skills/${id}/activate`, { method: "POST" });
}

// The dialog only opens on archived rows, but the checks run again server-side:
// a binding written since the listing rendered refuses in place.
export async function deleteSkillAction(id: number): Promise<ConfirmRefusal> {
  const result = await apiWrite(`/skills/${id}`, { method: "DELETE" });

  if (result.isOk) {
    return null;
  }

  const slug = result.problem.type.replace("/problems/", "");

  if (slug === "in-use") {
    return {
      refusal: `Quest templates still bind this skill: ${formatTemplateRefs(result.problem.templates ?? [])}. Delete or unbind them first.`,
    };
  }

  return {
    refusal:
      slug === "not-found"
        ? "This skill no longer exists."
        : "This skill has been restored since the page loaded. Archive it again first.",
  };
}
